import { useEffect } from 'react';
import { useSnackbar } from 'notistack';

import useReconnectPlaidAccount from 'modules/common/hooks/useReconnectPlaidAccount';
import { BaseTaskContext, SubmitActionTask } from 'modules/Tasks/types';
import useInvalidateMemberTaskQueries from 'services/memberTasks/useInvalidateMemberTaskQueries';

interface ReconnectPlaidContext extends BaseTaskContext {
    plaid_item_id: string;
    institution?: string;
    plaid_error_code?: string | null;
    plaid_error_code_reason?: string | null;
    plaid_error_type?: string | null;
    plaid_error_display_message?: string | null;
}

interface ReconnectPlaidModalProps {
    open: boolean;
    onClose: () => void;
    task: SubmitActionTask;
}

function isReconnectPlaidContext(ctx: unknown): ctx is ReconnectPlaidContext {
    return (
        typeof ctx === 'object' && ctx !== null && typeof (ctx as Record<string, unknown>).plaid_item_id === 'string'
    );
}

interface ReconnectPlaidModalContentProps {
    open: boolean;
    onClose: () => void;
    context: ReconnectPlaidContext;
    task: SubmitActionTask;
}

function ReconnectPlaidModalContent({ open, onClose, context, task }: Readonly<ReconnectPlaidModalContentProps>) {
    const invalidateMemberTaskQueries = useInvalidateMemberTaskQueries();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    useReconnectPlaidAccount({
        plaidItemId: open ? context.plaid_item_id : undefined,
        onPlaidModalOpen: () => {
            closeSnackbar();
        },
        onSuccess: () => {
            invalidateMemberTaskQueries(task);
            onClose();
        },
        onExit: () => {
            onClose();
        },
        onError: (message) => {
            enqueueSnackbar(message, { variant: 'error' });
        },
    });

    useEffect(() => {
        if (!open) {
            return;
        }
        enqueueSnackbar('Connecting you to Plaid...', {
            variant: 'info',
            preventDuplicate: true,
        });
    }, [open, enqueueSnackbar]);

    // The useReconnectPlaidAccount hook drives the Plaid Link popup directly;
    // this component renders no dialog of its own.
    return null;
}

export default function ReconnectPlaidModal({ open, onClose, task }: Readonly<ReconnectPlaidModalProps>) {
    const context = isReconnectPlaidContext(task.context) ? task.context : null;

    if (!context) {
        return null;
    }

    return <ReconnectPlaidModalContent open={open} onClose={onClose} context={context} task={task} />;
}
